import * as React from 'react';
import { TranslateFn, UnsubscribeFn, PresenceData, Status, OnlineStatusType} from '@wix/answers-app-core';
import { ChatRoomHeader } from './chat-room-header';
import { ChatTimeline } from './chat-timeline';
import { BaseWidgetProps } from '../../widget-container';
import { WidgetModuleType, ChatMessageType, User, UserBuilder, WidgetChatSettings,
	ActivityEventType } from '@wix/answers-api';
import { ChatInput } from './chat-input/index';
import { ChatTimelineItem, ChatMessage, RawChatTimelineEvent, ChatMessageStatus, ChatTimelineMessage,
	ChatTyping, UserTimelineType, ChatTimelineReply, RawChatMessage, ChatTimelineEvent } from './chat-timeline/types';
import { uploadFile, isMessage } from './utils';
import { ChatMessageBuilder } from './chat-timeline/chat-message.builder';
import { getProcessedMessages } from './chat-messages-processor';
import { WidgetMessageType, WidgetMessage, ChatRoomPreviewMode } from '../../../common/message-types';
import { getSystemAvatar } from './system-avatar';
import { isChatOpen } from '../../../common/is-chat-open';
import { saveChat, getChats, updateChat, clearChats } from '../../storage';
import { EventType } from '../../../common/logger/event-type';
import { ChatUserTimeline } from '../../api';

const ONLINE_EVENT_INTERVAL = 60 * 1000;
const SYSTEM_USER_ID = 'System';
const SYSTEM_TYPING_TIMEOUT = 1500;
const MAX_FILE_SIZE = 15 * 1024 * 1024;
export const chatRoomKey = 'chat-room';
const supportedFileTypes = ['.jpg', '.jpeg', '.png', '.pdf', '.docx', '.doc', '.xls', '.xlsx', '.csv'];
const getSystemUser = (color: string) => new UserBuilder()
	.withFirstName(SYSTEM_USER_ID)
	.withFullName(SYSTEM_USER_ID)
	.withUserId(SYSTEM_USER_ID)
	.withProfileImage(getSystemAvatar(color))
	.build();

export type ChatRoomProps = {
	// in
	baseWidgetProps: BaseWidgetProps;
	previewMode?: ChatRoomPreviewMode;
};

const translationPrefix = `widget-ng.chat.${chatRoomKey}`;

export type ChatRoomState = {
	inputValue: string;
	rawMessages: ChatTimelineReply[];
	systemMessages: ChatTimelineReply[];
	messages: ChatTimelineItem[];
	typing: ChatTyping[];
	activeAgents: User[];
	participants: User[];
	customFields?: { [key: string]: string };
	userId: string;
	ticketId: string;
	email?: string;
	name?: string;
	systemMessageSent: boolean;
	emailFormPending: boolean;
	cancelInProgressUpload: boolean;
	settings: WidgetChatSettings;
	windowWidth: number;
	windowHeight: number;
	startingChat: boolean;
	loading?: boolean;
};

export class ChatRoom extends React.PureComponent<ChatRoomProps, ChatRoomState> {
	unsubscribeUserTimeline: UnsubscribeFn;
	unsubscribeStatus: UnsubscribeFn;
	onlineInterval: any = null;
	timelineRef: ChatTimeline;

	state: ChatRoomState = {
		inputValue: '',
		rawMessages: [],
		systemMessages: [],
		messages: [],
		activeAgents: [],
		participants: [],
		customFields: this.props.baseWidgetProps.messageHistory.getCustomFields(),
		userId: '',
		ticketId: '',
		typing: [],
		systemMessageSent: false,
		emailFormPending: false,
		cancelInProgressUpload: false,
		settings: this.props.baseWidgetProps.widgetConfig.chatModuleSettings,
		windowWidth: 0,
		windowHeight: 0,
		email: this.props.baseWidgetProps.messageHistory.getUserEmail(),
		name: this.props.baseWidgetProps.messageHistory.getUserFirstName(),
		startingChat: false
	};

	componentDidMount () {
		const {isPreview, chatApi, logger, widgetId, locale} = this.props.baseWidgetProps;
		chatApi.getActiveChatAgents().then((activeAgents) => this.setState({activeAgents}));

		if (isPreview) {
			this.maybeSendSystemMessages();
			return;
		}

		this.props.baseWidgetProps.logger.logEvent(EventType.CHAT_VIEW, {});

		const savedChats = getChats(widgetId);
		if (savedChats.length) {
			const {ticketId, userId, systemMessages} = savedChats[0];
			this.setState({systemMessageSent: true, loading: true});
			const maybeTimeoutToAddAuthFn = !this.props.baseWidgetProps.messageHistory.isVisible() ? 1000 : 0;
			setTimeout(() => {
				chatApi.getChatTimeline(ticketId).then(async (chatTimeline) => {
					logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE, {widgetId, locale, ticketId, userId});
					this.setState({ticketId, userId, systemMessages});
					try {
						this.unsubscribeUserTimeline = await chatApi.subscribeToUserTimeline(
							ticketId, this.onReceiveMessage, this.handleTimelineUpdate);
						logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE_SUCCESS, {widgetId, locale, ticketId, userId});
					} catch (error) {
						logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE_FAIL, {widgetId, locale, ticketId, userId});
						throw error;
					}
					chatApi.presence.setOnline(ticketId, userId, window, document);
					this.startOnlineEventInterval();
					this.unsubscribeStatus = await chatApi.presence.subscribeToStatus(ticketId, this.handleTyping, []);
					this.handleTimelineUpdate(chatTimeline);
				}).catch((e) => {
					console.info(`Unable to restore chat: ${e}`);
					this.setState({
							systemMessageSent: false,
							loading: false,
							systemMessages: [],
							ticketId: '',
							userId: ''
						}, this.maybeSendSystemMessages);
				});
			}, maybeTimeoutToAddAuthFn);
		} else {
			this.maybeSendSystemMessages();
		}

		if (window) {
			window.addEventListener('message', this.handleSettingsPostMessage);
			window.addEventListener('resize', this.onWindowResize);
			window.addEventListener('beforeunload', this.handleUnload);
			this.setState({windowWidth: window.innerWidth, windowHeight: window.innerHeight});
		}
		if (document) {
			document.addEventListener('visibilitychange', this.handleVisibilityChange);
		}

		parent.postMessage({__ansWidget: {type: WidgetMessageType.ON_CHAT_LOADED, data: {}}}, '*');
	}

	componentWillUnmount () {
		if (this.unsubscribeStatus) {
			this.unsubscribeStatus();
		}
		if (this.unsubscribeUserTimeline) {
			this.unsubscribeUserTimeline();
		}
		if (window) {
			window.removeEventListener('message', this.handleSettingsPostMessage);
		}
		clearInterval(this.onlineInterval);
	}

	componentDidUpdate (prevProps: ChatRoomProps) {
		if (prevProps.previewMode !== this.props.previewMode) {
			this.setState({messages: [], rawMessages: [], systemMessageSent: false, emailFormPending: false}, () => {
				this.maybeSendSystemMessages();
			});
		}
	}

	startOnlineEventInterval = () => {
		const {widgetId, locale, logger} = this.props.baseWidgetProps;
		this.onlineInterval = setInterval(() => {
			const {userId, ticketId} = this.state;
			logger.logEvent(EventType.CHAT_ONLINE, {widgetId, locale, userId, ticketId, hidden: document.hidden});
		}, ONLINE_EVENT_INTERVAL);
	}

	handleUnload = () => {
		const {widgetId, locale, logger} = this.props.baseWidgetProps;
		const {userId, ticketId} = this.state;
		logger.logEvent(EventType.CHAT_UNLOAD, {widgetId, locale, userId, ticketId, hidden: document.hidden});
	}

	handleVisibilityChange = () => {
		const {widgetId, locale, logger} = this.props.baseWidgetProps;
		const {userId, ticketId} = this.state;
		logger.logEvent(
			document.hidden ? EventType.CHAT_HIDDEN : EventType.CHAT_VISIBLE, {widgetId, locale, userId, ticketId});
	}

	onWindowResize = () => {
		this.setState({windowHeight: window.innerHeight, windowWidth: window.innerWidth});
	}

	handleTimelineUpdate = (chatTimeline: ChatUserTimeline) => {
		const {widgetId, locale, logger} = this.props.baseWidgetProps;
		const {ticketId, userId, systemMessages} = this.state;
		logger.logEvent(
			EventType.CHAT_TIMELINE_UPDATED,
			{widgetId, locale, userId, ticketId, hidden: document.hidden, length: chatTimeline.timelineItems.length}
		);
		const persistedMessages = chatTimeline.timelineItems.map((message): ChatMessage | ChatTimelineEvent => {
			if (isMessage(message)) {
				return {...message, userTimelineType: UserTimelineType.REPLY, status: ChatMessageStatus.SUCCESS};
			} else {
				return {...message, userTimelineType: UserTimelineType.EVENT};
			}
		});
		const participants = Array.from(
			new Map(persistedMessages.map((msg): [string, User] => [msg.user.id, msg.user])).values()
		).filter((user) => user.id !== userId);
		const rawMessages = [...systemMessages, ...persistedMessages];
		const messages = this.processMessages(rawMessages);
		this.setState({messages, rawMessages, ticketId, userId, participants, loading: false});
	}

	handleSettingsPostMessage = (event: MessageEvent) => {
		const newSettings: any = event.data.previewNewSettings;
		if (newSettings && newSettings.chatModule) {
			this.handleSettingsPreview(newSettings.chatModule);
		}
		const widgetMessage: WidgetMessage = event.data.__ansWidget;
		const firstOpen: boolean | undefined = event.data.firstOpen;
		if (widgetMessage) {
			switch (widgetMessage.type) {
				case WidgetMessageType.CLEAR_CHATS:
					clearChats(this.props.baseWidgetProps.widgetId);
					if (widgetMessage.data.endExistingChat) {
						const {userId, ticketId} = this.state;
						if (ticketId) {
							this.props.baseWidgetProps.chatApi.presence.setOffline(ticketId, userId);
						}
						if (this.unsubscribeStatus) {
							this.unsubscribeStatus();
						}
						if (this.unsubscribeUserTimeline) {
							this.unsubscribeUserTimeline();
						}
						this.setState({
							rawMessages: [],
							messages: [],
							participants: [],
							userId: '',
							ticketId: '',
							typing: [],
							systemMessageSent: false,
							emailFormPending: false,
						});
					}
					break;
				case WidgetMessageType.IDENTIFY_USER:
					const {name, email} = widgetMessage.data.userData;
					this.setState({email, name});
					break;
				case WidgetMessageType.SET_CUSTOM_FIELDS:
					this.setState({customFields: widgetMessage.data.customFields});
					break;
				case WidgetMessageType.SEND_CHAT_MESSAGE:
					this.addSystemTextMessage(widgetMessage.data.content);
					break;
			}
		}
		if (firstOpen && !this.props.baseWidgetProps.isPreview) {
			this.maybeSendSystemMessages();
			this.setState({windowWidth: window.innerWidth, windowHeight: window.innerHeight});
		}
	}

	tp: TranslateFn = (key, params) => {
		return this.props.baseWidgetProps.translate(`${translationPrefix}.${key}`, params);
	}

	processMessages = (messages: ChatTimelineReply[]) => {
		const {locale, translate} = this.props.baseWidgetProps;
		const localizedTimeStrings = {
			today: translate('widget-ng.common.today'),
			yesterday: translate('widget-ng.common.yesterday')
		};
		return getProcessedMessages(messages, locale, localizedTimeStrings);
	}

	getBusinessHoursDependentSettings = () => {
		const {isPreview} = this.props.baseWidgetProps;
		const {previewMode} = this.props;
		const {duringBusinessHoursSettings, outsideBusinessHoursSettings, chatContentSettings} = this.state.settings;
		const {responseTimeMessage, responseTimeMessageEnabled} = chatContentSettings;
		const useDuringHoursSettings = (isPreview && previewMode === 'during-hours') || (!isPreview && !this.isChatClosed());
		const useOutsideHoursSettings = (isPreview && previewMode === 'outside-hours')
			|| (!isPreview && this.isChatClosed());

		if (useDuringHoursSettings) {
			return {
				...duringBusinessHoursSettings,
				responseTimeMessage,
				responseTimeMessageEnabled
			};
		} else if (useOutsideHoursSettings) {
			const {overrideResponseTimeMessageEnabled, awayMessage, ...rest} = outsideBusinessHoursSettings.contactViaChat;
			const awayOrDefaultMessage = awayMessage || this.tp('defaults.outside-hours.response-time');
			return {
				...rest,
				responseTimeMessage: overrideResponseTimeMessageEnabled ? awayOrDefaultMessage : responseTimeMessage,
				responseTimeMessageEnabled: overrideResponseTimeMessageEnabled ? true : responseTimeMessageEnabled
			};
		} else {
			return {
				...duringBusinessHoursSettings,
				responseTimeMessage,
				responseTimeMessageEnabled,
				requestEmailEnabled: false,
				greetingMessageEnabled: false
			};
		}
	}

	handleSettingsPreview = (settings: WidgetChatSettings) => {
		this.setState({settings}, () => {
			this.setState({messages: [], rawMessages: [], systemMessageSent: false, emailFormPending: false}, () => {
				this.maybeSendSystemMessages(true);
			});
		});
	}

	maybeSendSystemMessages = async (disableTyping?: boolean) => {
		const isWidgetOpen = this.props.baseWidgetProps.messageHistory.isVisible() || this.props.baseWidgetProps.isPreview;
		if (isWidgetOpen && !this.state.systemMessageSent) {
			const {requestEmailEnabled} = this.getBusinessHoursDependentSettings();
			const isAuthenticated = this.props.baseWidgetProps.messageHistory.authenticatorAdded();
			const willRequestEmail = requestEmailEnabled && !this.state.email && !isAuthenticated;
			this.setState({systemMessageSent: true, emailFormPending: willRequestEmail});
			await this.maybeSendGreetingMessage(disableTyping);
			await this.maybeSendEmailForm(disableTyping);
		}
	}

	isChatClosed = () => {
		const {businessHours} = this.state.settings;
		return !isChatOpen(businessHours, this.props.baseWidgetProps.widgetConfig.tenant.timeZone);
	}

	maybeSendEmailForm = async (disableTyping?: boolean) => {
		const {requestEmailMessage, requestEmailEnabled} = this.getBusinessHoursDependentSettings();
		const isAuthenticated = this.props.baseWidgetProps.messageHistory.authenticatorAdded();
		if (requestEmailEnabled && !this.state.email && !isAuthenticated) {
			const message = requestEmailMessage || this.tp('defaults.during.email-message');
			await this.addSystemTextMessage(message, disableTyping);
			return this.addSystemMessage({
				user: getSystemUser(this.props.baseWidgetProps.widgetConfig.appearance.brandColor),
				userTimelineType: UserTimelineType.EMAIL_FORM,
				creationDate: Date.now()
			}, disableTyping);
		}
		return Promise.resolve({});
	}

	maybeSendGreetingMessage = (disableTyping?: boolean) => {
		const {greetingMessage, greetingMessageEnabled} = this.getBusinessHoursDependentSettings();
		if (greetingMessageEnabled) {
			const overrideGreetingMessage = this.props.baseWidgetProps.messageHistory.getGreetingMessageOverride();
			const message = overrideGreetingMessage || greetingMessage || this.tp('defaults.during.greeting-message');
			return this.addSystemTextMessage(message, disableTyping);
		}
		return Promise.resolve({});
	}

	onInputChange = (value: string) => {
		this.setState({ inputValue: value });
		if (this.state.ticketId) {
			this.props.baseWidgetProps.chatApi.presence.setInteracting(this.state.ticketId, this.state.userId, window);
		}
	}

	onReceiveMessage = (message: RawChatMessage | RawChatTimelineEvent) => {
		const {logger, locale, widgetId} = this.props.baseWidgetProps;
		const {ticketId, userId} = this.state;
		logger.logEvent(EventType.RECEIVE_CHAT_MESSAGE, {widgetId, locale, userId, ticketId, hidden: document.hidden});
		if (!isMessage(message) && message.eventType === ActivityEventType.CHAT_TICKET_USER_UPDATED) {
			return;
		}
		const rawMessages: ChatTimelineReply[] = [
			...this.state.rawMessages,
			isMessage(message) ? {...message, userTimelineType: UserTimelineType.REPLY, status: ChatMessageStatus.SUCCESS}
				: {...message, userTimelineType: UserTimelineType.EVENT}
		];
		const messages = this.processMessages(rawMessages);
		const participants = this.state.participants;
		const participantIds = participants.map((participant) => participant.id);
		if (!participantIds.includes(message.user.id)) {
			participants.push(message.user);
		}
		this.setState({ rawMessages, messages: [...messages, ...this.state.typing], participants });
	}

	handleTyping = (presenceMap: Map<string, PresenceData>) => {
		presenceMap.forEach((value, id) => {
			if (value.type === Status.ONLINE && value.data === OnlineStatusType.INTERACTING && value.user) {
				const user = value.user;
				const newTyping: ChatTyping = {userTimelineType: UserTimelineType.TYPING, user};
				this.setState({typing: [...this.state.typing, newTyping], messages: [...this.state.messages, newTyping]});
			} else {
				const newTyping: ChatTyping[] = this.state.typing.filter((typingItem) => typingItem.user.id !== id);
				this.setState({
					typing: newTyping,
					messages: this.state.messages.filter((message) =>
						!(message.userTimelineType === UserTimelineType.TYPING && message.user.id === id))
				});
			}
		});
	}

	maybeInitChat = async () => {
		if (this.state.ticketId) {
			return {ticketId: this.state.ticketId, userId: this.state.userId};
		}
		if (this.state.startingChat) {
			throw new Error('In process of starting chat');
		}

		this.setState({startingChat: true});
		const { widgetId, locale, chatApi, onChatStarted, logger } = this.props.baseWidgetProps;
		const customFields = this.state.customFields || {};
		const userEmail = this.state.email || '';
		const userFirstName = this.state.name || '';

		logger.logEvent(EventType.CHAT_STARTED, {widgetId, locale});
		try {
			const { ticketId, userId } = await chatApi.startChat({widgetId, locale, customFields, userEmail, userFirstName});
			this.startOnlineEventInterval();
			const systemMessages = this.state.rawMessages.filter((msg) => msg.user.id === SYSTEM_USER_ID);
			this.setState({ticketId, userId, systemMessages, startingChat: false});
			saveChat(widgetId, {ticketId, userId, systemMessages});
			logger.logEvent(EventType.CHAT_STARTED_SUCCESS, {widgetId, locale, userId, ticketId});
			logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE, {widgetId, locale, ticketId, userId});
			chatApi.subscribeToUserTimeline(
						ticketId, this.onReceiveMessage, this.handleTimelineUpdate).then((unsubUserTimeline) => {
							this.unsubscribeUserTimeline = unsubUserTimeline;
							logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE_SUCCESS, {widgetId, locale, ticketId, userId});
						}).then(() => {
							chatApi.presence.subscribeToStatus(ticketId, this.handleTyping, []).then((unsubPresence) => {
								this.unsubscribeStatus = unsubPresence;
								chatApi.presence.setOnline(ticketId, userId, window, document);
							});
						}).catch(() => {
							logger.logEvent(EventType.SUBSCRIBE_USER_TIMELINE_FAIL, {widgetId, locale, ticketId, userId});
						});
			onChatStarted(ticketId, userId);
			parent.postMessage({__ansWidget: {type: WidgetMessageType.ON_CHAT_STARTED, data: {ticketId}}}, '*');
			return {ticketId, userId};
		} catch (error) {
			this.setState({startingChat: false});
			logger.logEvent(EventType.CHAT_STARTED_FAIL, {widgetId, locale});
			throw error;
		}
	}

	onSubmitEmail = async (email: string) => {
		const {chatApi, logger, widgetId, locale} = this.props.baseWidgetProps;
		const {ticketId, userId} = this.state;
		logger.logEvent(EventType.SUBMIT_EMAIL_FORM, {widgetId, locale});
		if (ticketId && userId) {
			logger.logEvent(EventType.UPDATE_EMAIL, {widgetId, locale, userId, ticketId});
			try {
				await chatApi.updateEmail(userId, email);
				logger.logEvent(EventType.UPDATE_EMAIL_SUCCESS, {widgetId, locale, userId, ticketId});
			} catch (error) {
				// tslint:disable-next-line:no-console
				console.log(`Unable to update user email: ${error}`);
				logger.logEvent(EventType.UPDATE_EMAIL_FAIL, {widgetId, locale, userId, ticketId});
			}
		} else {
			this.setState({email}, this.maybeInitChat);
		}

		const updatedRawMessages = this.state.rawMessages.map((message) => {
			const isEmailForm = message.userTimelineType === UserTimelineType.EMAIL_FORM;
			return isEmailForm ? {...message, email} : message;
		});
		this.setState({
			email, rawMessages: updatedRawMessages, messages: this.processMessages(updatedRawMessages),
			emailFormPending: false, systemMessages: updatedRawMessages.filter((msg) => msg.user.id === SYSTEM_USER_ID)
		}, async () => {
			const thankYouMessage = this.getBusinessHoursDependentSettings().thanksMessage
			|| this.tp('defaults.during.thank-you-message');
			await this.addSystemTextMessage(thankYouMessage);
			this.updateChatStorage();
		});
	}

	updateChatStorage = () => {
		const {widgetId} = this.props.baseWidgetProps;
		const {rawMessages, ticketId, userId} = this.state;
		updateChat(widgetId, {ticketId, userId, systemMessages: rawMessages.filter((msg) => msg.user.id === SYSTEM_USER_ID)});
	}

	updateMessageStatus = (message: ChatMessage, status: ChatMessageStatus) => {
		const rawMessages: ChatTimelineReply[] = this.state.rawMessages.map((msg) => {
			if (msg.userTimelineType !== UserTimelineType.REPLY) {
				return msg;
			}
			return message.creationDate === msg.creationDate ? {...msg, status} : msg;
		});
		this.setState({rawMessages, messages: this.processMessages(rawMessages)});
	}

	onSend = async () => {
		const {chatApi, logger, widgetId, locale} = this.props.baseWidgetProps;
		const inputValue = this.state.inputValue;
		if (inputValue === '') {
			return;
		}
		const newMessage: ChatMessage = new ChatMessageBuilder().withText(inputValue)
			.withChatMessageType(ChatMessageType.USER).withStatus(ChatMessageStatus.SENDING).build();
		const rawMessages = [...this.state.rawMessages, newMessage];
		const messages = this.processMessages(rawMessages);
		this.setState({ rawMessages, messages: [...messages, ...this.state.typing], inputValue: '' });
		try {
			const {ticketId, userId} = await this.maybeInitChat();
			logger.logEvent(EventType.SEND_CHAT_MESSAGE, {widgetId, locale, userId, ticketId});
			chatApi.presence.endInteracting(ticketId, this.state.userId);
			return chatApi.sendChatMessage(ticketId, inputValue)
				.then(() => {
					logger.logEvent(EventType.SEND_CHAT_MESSAGE_SUCCESS, {widgetId, locale, userId, ticketId});
					this.updateMessageStatus(newMessage, ChatMessageStatus.SUCCESS);
				})
				.catch(() => {
					logger.logEvent(EventType.SEND_CHAT_MESSAGE_FAIL, {widgetId, locale, userId, ticketId});
					this.updateMessageStatus(newMessage, ChatMessageStatus.FAIL);
				});
		} catch (error) {
			logger.logEvent(EventType.SEND_CHAT_MESSAGE_FAIL, {widgetId, locale});
			this.updateMessageStatus(newMessage, ChatMessageStatus.FAIL);
		}
	}

	onResendMessage = async (message: ChatTimelineMessage): Promise<void> => {
		const {chatApi, logger, widgetId, locale} = this.props.baseWidgetProps;
		try {
			const {ticketId, userId} = await this.maybeInitChat();
			logger.logEvent(EventType.SEND_CHAT_MESSAGE, {widgetId, locale, userId, ticketId});
			return (
				message.chatMessageData ? chatApi.sendChatFile(ticketId, message.chatMessageData.attachments)
					: chatApi.sendChatMessage(ticketId, message.text)
				).then(() => {
					logger.logEvent(EventType.SEND_CHAT_MESSAGE_SUCCESS, {widgetId, locale, userId, ticketId});
					this.updateMessageStatus(message, ChatMessageStatus.SUCCESS);
				})
				.catch(() => {
					logger.logEvent(EventType.SEND_CHAT_MESSAGE_FAIL, {widgetId, locale, userId, ticketId});
					this.updateMessageStatus(message, ChatMessageStatus.FAIL);
				});
		} catch (e) {
			logger.logEvent(EventType.SEND_CHAT_MESSAGE_FAIL, {widgetId, locale});
			this.updateMessageStatus(message, ChatMessageStatus.FAIL);
		}
	}

	addSystemMessage = async (message: ChatTimelineReply, disableTyping?: boolean) => {
		return new Promise<void>((res) => {
			if (disableTyping) {
				const newRawMessages = [...this.state.rawMessages, message];
				const messages = [...this.processMessages(newRawMessages), ...this.state.typing];
				this.setState({messages, rawMessages: newRawMessages});
				res();
			} else {
				const {brandColor} = this.props.baseWidgetProps.widgetConfig.appearance;
				const systemTyping: ChatTyping = {userTimelineType: UserTimelineType.TYPING, user: getSystemUser(brandColor)};
				this.setState({typing: [...this.state.typing, systemTyping], messages: [...this.state.messages, systemTyping]});
				setTimeout(() => {
					const newTyping = this.state.typing.filter(({user}) => user.firstName !== SYSTEM_USER_ID);
					const newRawMessages = [...this.state.rawMessages, message];
					const messages = [...this.processMessages(newRawMessages), ...newTyping];
					this.setState({typing: newTyping, messages, rawMessages: newRawMessages});
					res();
				}, SYSTEM_TYPING_TIMEOUT);
			}
		});
	}

	addSystemTextMessage = async (text: string, disableTyping?: boolean) => {
		const {brandColor} = this.props.baseWidgetProps.widgetConfig.appearance;
		const message = new ChatMessageBuilder().withText(text).withChatMessageType(ChatMessageType.AGENT)
			.withUserId(SYSTEM_USER_ID).withUser(getSystemUser(brandColor)).build();
		return this.addSystemMessage(message, disableTyping);
	}

	handleSelectFile = async (file: File, cb: (percent: number) => void) => {
		const { apiUrl, chatApi } = this.props.baseWidgetProps;
		this.timelineRef.scrollToBottom();
		const {ticketId} = await this.maybeInitChat();
		const callback = (percent: number) => {
			if (!this.state.cancelInProgressUpload) {
				cb(percent);
			}
		};
		return uploadFile(apiUrl, file, callback)
			.then(({ filename, filelink, fileSizeBytes, contentType }) => {
				if (!this.state.cancelInProgressUpload) {
					const attachments = [{ name: filename, url: filelink, sizeBytes: fileSizeBytes, contentType }];
					const newMessage: ChatMessage = new ChatMessageBuilder().withChatMessageData({ attachments })
						.withChatMessageType(ChatMessageType.USER).withStatus(ChatMessageStatus.SENDING).build();
					const rawMessages = [...this.state.rawMessages, newMessage];
					const messages = this.processMessages(rawMessages);
					this.setState({ rawMessages, messages: [...messages, ...this.state.typing]});
					chatApi.sendChatFile(
						ticketId,
						[{ name: filename, url: filelink, sizeBytes: fileSizeBytes, contentType }]
					).then(() => this.updateMessageStatus(newMessage, ChatMessageStatus.SUCCESS))
					.catch(() => this.updateMessageStatus(newMessage, ChatMessageStatus.FAIL));
				} else {
					this.setState({cancelInProgressUpload: false});
				}
			})
			.catch((reason) => {
				if (!this.state.cancelInProgressUpload) {
					throw new Error(reason);
				} else {
					this.setState({cancelInProgressUpload: false});
				}
			});
	}

	setTimelineRef = (timeline: ChatTimeline) => {
		this.timelineRef = timeline;
	}

	onCancelFileUpload = () => this.setState({cancelInProgressUpload: true});

	render () {
		const { props, state } = this;
		const { widgetConfig } = props.baseWidgetProps;

		const {inputPlaceHolder} = state.settings.chatContentSettings;
		const placeholder = inputPlaceHolder || props.baseWidgetProps.translate('widget-ng.chat.defaults.input-placeholder');
		const onBack = () => props.baseWidgetProps.widgetNavigator.switchModules(WidgetModuleType.KB);

		const hoursSettings = this.getBusinessHoursDependentSettings();
		const headerConfig = {
			...state.settings.chatContentSettings,
			responseTimeMessage: hoursSettings.responseTimeMessage
		};

		const input = this.state.emailFormPending && !hoursSettings.requestEmailOptional ?
			(
				<div className='input-block'>
					{this.props.baseWidgetProps.translate('widget-ng.chat.contact-details.input-block')}
				</div>
			) : (
				<ChatInput
					value={state.inputValue}
					placeholder={placeholder}
					onChange={this.onInputChange}
					onSend={this.onSend}
					t={props.baseWidgetProps.translate}
					uploadLimitInBytes={MAX_FILE_SIZE}
					supportedFileTypes={supportedFileTypes}
					onSelectFile={this.handleSelectFile}
					onCancel={this.onCancelFileUpload}
					disabled={state.loading}
				/>
		);

		return (
			<div className={chatRoomKey}>
				<ChatRoomHeader
					config={headerConfig}
					backgroundColor={widgetConfig.appearance.brandColor}
					fontFamily={widgetConfig.appearance.font.name}
					participants={this.state.participants}
					messageCount={0}
					activeAgents={this.state.activeAgents}
					onBack={onBack}
					hideBack={!widgetConfig.modulesConfig.knowledgebaseEnabled}
					t={this.props.baseWidgetProps.translate}
					windowHeight={state.windowHeight}
					windowWidth={state.windowWidth}
					isMobile={props.baseWidgetProps.isMobile}
				/>
				<ChatTimeline
					ref={this.setTimelineRef}
					items={state.messages}
					onSubmitEmail={this.onSubmitEmail}
					t={props.baseWidgetProps.translate}
					isMobile={this.props.baseWidgetProps.isMobile}
					isEmailOptional={hoursSettings.requestEmailOptional}
					onResendMessage={this.onResendMessage}
					loading={state.loading}
				/>
				{input}
			</div>
		);
	}
}
